import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../../utils/api';
import { useApp } from './useApp';

/**
 * Sprint 5.bis.3 — Hook único para obtener el contexto del despacho del
 * user autenticado (firm membership + datos del despacho).
 *
 * Reusa `GET /api/accounting-firms/me` (entregado en Sprint 5.bis.0.3) en
 * lugar de extender otros endpoints. Single source of truth para "soy
 * asesor" + datos del firm (nombre, CIF, etc.).
 *
 * Response shape (BE):
 *   { firm: { id, name, cif, contact_email, contact_phone } | null,
 *     role: 'owner' | 'asesor' | 'viewer' | null,
 *     member_since: string | null,
 *     companies: [{ id, name, cif }] }
 *
 * Notas:
 *   - `firmRole` (NO `role`): el rol DENTRO del firm es semánticamente
 *     distinto de `user.role` / `effectiveRole`. Renombrado para evitar
 *     colisiones peligrosas en callers.
 *   - QueryKey scoped por `user.id` (no por `company.id`) — la firm
 *     membership NO cambia al hacer switchCompany.
 *   - staleTime 5 min: cambios en membership son raros y se invalidan
 *     manualmente desde mutaciones (invitation accept, member remove).
 *   - Returns conservadores durante loading (`firm: null`, `isAsesor:
 *     false`) — el caller renderiza nothing/empty state sin flash.
 */
interface AccountingFirmContext {
  firm: {
    id: string;
    name: string;
    cif: string | null;
    contact_email: string | null;
    contact_phone: string | null;
    // SILTRA A.3 (#576) — Nº Autorización RED de la asesoría; el backend
    // `GET /accounting-firms/me` ya lo devuelve. Editable en /portal-asesor/configuracion.
    red_authorization_number: string | null;
  } | null;
  firmRole: 'owner' | 'asesor' | 'viewer' | null;
  isAsesor: boolean;
  /** Nº de empresas-cliente de la cartera (Modo Asesor v2 — cabecera del shell). */
  companiesCount: number;
  isLoading: boolean;
}

interface AccountingFirmsMeResponse {
  firm: AccountingFirmContext['firm'];
  role: AccountingFirmContext['firmRole'];
  member_since?: string | null;
  companies?: Array<{ id: string; name: string; cif: string | null }>;
}

export function useAccountingFirmContext(): AccountingFirmContext {
  const { user } = useApp();

  const { data, isLoading } = useQuery<AccountingFirmsMeResponse>({
    queryKey: ['accounting-firms-me', user?.id],
    queryFn: () => apiFetch<AccountingFirmsMeResponse>('/api/accounting-firms/me'),
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  return {
    firm: data?.firm ?? null,
    firmRole: data?.role ?? null,
    isAsesor: !!data?.firm,
    companiesCount: data?.companies?.length ?? 0,
    isLoading,
  };
}

export default useAccountingFirmContext;
